import { useEffect, useState } from 'react';
import { FormControl, Box } from '@mui/material';
import { getAIAssistantList } from '../../../Pages/AIAssistant/actions';
import { useSelector } from 'react-redux';
import colors from '../../../Utils/colors';
import { toast } from 'react-toastify';
import { eventsend } from '../../../Config/analyticsFunctions';
import { Form, InputGroup } from 'react-bootstrap';

interface Assistant {
    _id: string;
    id: string;
    name: string;
    ai_model?: string;
    model?: string;
    tokens?: number;
    triggered?: number;
    createdAt?: string;
    updatedAt?: string;
}

interface AIAssistantNodeModalProps {
    data: {
        assistant?: Assistant;
        exitKeyword?: string;
        initialContext?: string;   // NEW FIELD
        enableBuffering?: boolean;
        bufferDurationSeconds?: number;
    };
    setData: (data: any) => void;
    setBtnDisabled: (disabled: boolean) => void;
}

const AIAssistantNodeModal: React.FC<AIAssistantNodeModalProps> = ({ data, setData, setBtnDisabled }) => {
    const authtoken = useSelector((state: any) => state.main.auth_token);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [assistantsList, setAssistantsList] = useState<Assistant[]>([]);
    const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
    const [exitKeyword, setExitKeyWord] = useState('');
    const [initialContext, setInitialContext] = useState(data?.initialContext || ''); // NEW STATE
    const workflowReservedWords = ['exit', 'back', 'prev', 'menu'];
    const [enableBuffering, setEnableBuffering] = useState<boolean>(data?.enableBuffering || false);
    const [bufferDuration, setBufferDuration] = useState<number>(data?.bufferDurationSeconds || 30);

    useEffect(() => {
        fetchAssistants();
        setExitKeyWord(data?.exitKeyword || '');
        setInitialContext(data?.initialContext || ''); // SET FROM PROPS
        setEnableBuffering(data?.enableBuffering || false);
        setBufferDuration(data?.bufferDurationSeconds || 30);
    }, []);

    const fetchAssistants = async () => {
        try {
            setIsLoading(true);
            const response = await getAIAssistantList(authtoken);

            if (response?.data?.data) {
                const formattedAssistants = response.data.data.map((item: any) => ({
                    ...item,
                    id: item._id
                }));
                setAssistantsList(formattedAssistants);
                if (data?.assistant) {
                    setSelectedAssistant(data.assistant);
                }
            } else {
                toast.error('Failed to load assistants');
                setAssistantsList([]);
            }
        } catch (error) {
            console.error('Error fetching assistants:', error);
            toast.error('An error occurred while fetching assistants');
            setAssistantsList([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (assistantsList.length > 0 && data?.assistant) {
            const matchedAssistant = assistantsList.find(
                (a) => a._id === data?.assistant?._id || a.id === data?.assistant?.id
            );
            setSelectedAssistant(matchedAssistant || null);
        }
    }, [assistantsList, data.assistant]);

    useEffect(() => {
        if (selectedAssistant) {
            setData({
                ...data,
                assistant: selectedAssistant,
                assistantId: selectedAssistant._id || selectedAssistant.id,
                exitKeyword,
                initialContext, // SAVE NEW FIELD
                enableBuffering,
                bufferDurationSeconds: bufferDuration,
            });
            setBtnDisabled(false);
        } else {
            setBtnDisabled(true);
        }
    }, [selectedAssistant, exitKeyword, initialContext, enableBuffering, bufferDuration]);


    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {/* Select Assistant */}
            <FormControl fullWidth sx={{ mb: 3 }}>
                <label htmlFor="assistant-select-label">AI Assistant</label>
                <select
                    id="assistant-select-label"
                    value={selectedAssistant?._id || selectedAssistant?.id || ''}
                    onChange={(e) => {
                        const assistant = assistantsList.find(a => a._id === e.target.value || a.id === e.target.value);
                        setSelectedAssistant(assistant || null);
                        if (assistant) {
                            eventsend('ai_assistant_selected_in_node', {
                                timestamp: new Date().toISOString(),
                                assistant_id: assistant._id || assistant.id,
                                assistant_name: assistant.name,
                                platform: 'web'
                            });
                        }
                    }}
                    disabled={isLoading}
                    style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        fontSize: '14px',
                        color: '#333',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">Select</option>
                    {assistantsList.length > 0 ? (
                        assistantsList.map((assistant) => (
                            <option key={assistant._id || assistant.id} value={assistant._id || assistant.id}>
                                {assistant.name} ({assistant.ai_model || assistant.model || 'No model'})
                            </option>
                        ))
                    ) : (
                        <option disabled>No assistants found</option>
                    )}
                </select>
            </FormControl>

            {/* Initial Context */}
            <FormControl fullWidth sx={{ mb: 3 }}>
                <label>Initial Context</label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Add some conversation context for AI assistant to consider"
                    value={initialContext}
                    onChange={(e) => setInitialContext(e.target.value)}
                    style={{ borderRadius: 8 }}
                />
                <Form.Text style={{ fontSize: 12, color: colors.greys04 }}>
                    Setting the initial context helps the AI assistant understand the ongoing conversation better. You can provide metadata for the AI assistant to reference.
                </Form.Text>
                <Form.Text style={{ fontSize: 12, color: colors.greys04 }}>
                    Note: You can also use %var% or @var to insert variables in the initial context.
                </Form.Text>
            </FormControl>

            {/* Exit Phrase */}
            <FormControl fullWidth sx={{ mb: 3 }}>
                <label>Exit Phrase</label>
                <Form.Control
                    type="text"
                    placeholder="Exit phrase to return to workflow"
                    value={exitKeyword}
                    onChange={(e) => {
                        const containsReservedWord = workflowReservedWords.some(word =>
                            e.target.value.toLowerCase().includes(word)
                        );
                        if (containsReservedWord) {
                            toast.error("Exit phrase cannot include exit, back, prev and menu.");
                        } else {
                            setExitKeyWord(e.target.value);
                            if (e.target.value.trim()) {
                                eventsend('ai_assistant_exit_phrase_set', {
                                    exit_phrase: e.target.value,
                                    is_valid_exit_phrase: !containsReservedWord,
                                    assistant_id: selectedAssistant?._id || selectedAssistant?.id || '',
                                    platform: 'web'
                                });
                            }
                        }
                    }}
                    style={{ borderRadius: 8 }}
                />
                <Form.Text style={{ fontSize: 12, color: colors.greys04 }}>
                    Note - Do not use exit, menu, back and prev in exit phrase
                </Form.Text>
            </FormControl>

            {/* Enable Buffering */}
            <InputGroup className="mb-3" style={{ width: '100%', marginBlock: 10 }}>
                <Form.Check
                    type="checkbox"
                    label="Enable Message Buffering"
                    checked={enableBuffering}
                    onChange={(e) => {
                        setEnableBuffering(e.target.checked);
                        eventsend('ai_assistant_buffering_toggled', {
                            enabled: e.target.checked,
                            assistant_id: selectedAssistant?._id || selectedAssistant?.id || '',
                            platform: 'web'
                        });
                    }}
                />
            </InputGroup>

            {enableBuffering && (
                <InputGroup className="mb-3" style={{ width: '100%', marginBlock: 10 }}>
                    <Form.Label style={{ fontSize: 16, fontWeight: 600, marginTop: 10, marginRight: 10 }}>
                        Buffer Duration (seconds)
                    </Form.Label>
                    <Form.Control
                        type="number"
                        min={1}
                        max={360}
                        value={bufferDuration}
                        onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setBufferDuration(isNaN(val) ? 30 : val);
                            eventsend('ai_assistant_buffer_duration_set', {
                                buffer_duration: val,
                                assistant_id: selectedAssistant?._id || selectedAssistant?.id || '',
                                platform: 'web'
                            });
                        }}
                        style={{ borderRadius: 8 }}
                    />
                </InputGroup>
            )}
            <Form.Text style={{ fontSize: 12, color: colors.greys04 }}>
                {`When enabled, AI Assistant will wait for ${bufferDuration} seconds to collect multiple messages before responding.`}
            </Form.Text>
        </Box>
    );
};

export default AIAssistantNodeModal;
